import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../lib/AuthContext";
import {
  subscribeTransactions, subscribeNotifications,
  addMoney, sendPayment, updateUserProfile,
  markAllNotificationsRead, db,
  setPin, verifyPin, hasPin
} from "../lib/db";
import { calcFee, feeLabel, countdown, timeAgo } from "../lib/utils";
import { collection, query, where, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
import { updateEmail, updatePassword, sendEmailVerification, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";
import { auth } from "../lib/firebase";

/* ── Design tokens (match index.css) ─────────────────────────── */
const T = {
  ink:"#0A0F1E", mut:"#5B6B8C", faint:"#98A2B8",
  mist:"#F4F6FA", line:"#E5EAF3", line2:"#EEF1F7",
  green:"#0E9F6E", greenBg:"#E6F7EF",
  amber:"#B45309", amberBg:"#FDF1DE",
  red:"#DC2626", redBg:"#FDECEC", blue:"#2563EB",
};
const H1 = { fontWeight:900, fontSize:22, letterSpacing:-0.8, color:T.ink };
const SUB = { fontSize:12, color:T.faint, marginTop:3 };

/* Small shared pieces */
const SectionLabel = ({ children }) => (
  <p className="sec-label">{children}</p>
);
const IconTile = ({ bg, children, size = 44 }) => (
  <div style={{ width:size, height:size, background:bg, borderRadius:14, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{children}</div>
);

// ── Plaid Link Button Component ───────────────────────────────
// Calls our backend to get a link token, then opens Plaid Link
const BACKEND_URL = "https://xylopay-backend.onrender.com"; // ← update after deploying to Render

function PlaidLinkButton({ profile, refreshProfile, toast$, updateUserProfile }) {
  const [linking, setLinking] = useState(false);

  const openPlaidLink = async () => {
    setLinking(true);
    try {
      // Step 1: Get link token from our backend
      const res = await fetch(`${BACKEND_URL}/create_link_token`, {
        method: "POST",
        headers: { "Content-Type":"application/json" },
        body: JSON.stringify({ userId: profile.id }),
      });
      const { link_token, error } = await res.json();
      if (error) throw new Error(error);

      // Step 2: Open Plaid Link with the token
      // Plaid Link is loaded via script tag in index.html
      const handler = window.Plaid.create({
        token: link_token,
        onSuccess: async (public_token, metadata) => {
          try {
            // Step 3: Exchange public token for access token
            const exchRes = await fetch(`${BACKEND_URL}/exchange_token`, {
              method: "POST",
              headers: { "Content-Type":"application/json" },
              body: JSON.stringify({ public_token, userId: profile.id }),
            });
            const data = await exchRes.json();
            if (data.error) throw new Error(data.error);

            // Step 4: Save bank info to Firebase
            const primaryAccount = data.accounts[0];
            const bank = {
              name:        data.bank_name,
              last4:       primaryAccount.mask,
              type:        primaryAccount.subtype,
              accountId:   primaryAccount.id,
              plaidItemId: data.item_id,
            };
            await updateUserProfile(profile.id, {
              bank,
              plaidAccessToken: data.access_token, // store securely
            });
            await refreshProfile();
            toast$(`${data.bank_name} linked successfully! 🏦`);
          } catch(e) {
            toast$("Failed to save bank. Try again.", "error");
          }
        },
        onExit: (err) => {
          if (err) toast$("Bank linking cancelled.", "error");
          setLinking(false);
        },
        onLoad: () => setLinking(false),
      });
      handler.open();
    } catch(e) {
      console.error("Plaid link error:", e);
      toast$("Failed to open bank linking. Try again.", "error");
      setLinking(false);
    }
  };

  return (
    <button className="btn btn-primary" onClick={openPlaidLink} disabled={linking}
      style={{ width:"100%", padding:"16px", fontSize:14 }}>
      {linking ? (
        <><span className="spinner" style={{ borderTopColor:"#fff", borderColor:"rgba(255,255,255,.25)" }}/> Connecting...</>
      ) : (
        <>🏦 Connect Bank Account</>
      )}
    </button>
  );
}

function ContactForm({ profile, toast$ }) {
  const [msg, setMsg]       = useState({ subject:"", message:"" });
  const [sending, setSending] = useState(false);
  const [sent, setSent]     = useState(false);

  const doSend = async () => {
    if (!msg.subject || !msg.message) return toast$("Fill all fields","error");
    setSending(true);
    try {
      await addDoc(collection(db, "support_messages"), {
        from:      profile?.name || "Unknown",
        email:     profile?.email || "",
        userId:    profile?.userId || "",
        subject:   msg.subject,
        message:   msg.message,
        createdAt: serverTimestamp(),
        status:    "unread",
      });
      setSent(true);
      setMsg({ subject:"", message:"" });
      toast$("Message sent! We'll reply within 24 hours ✓");
    } catch(e) { toast$("Failed to send. Try emailing us directly.","error"); }
    finally { setSending(false); }
  };

  if (sent) return (
    <div style={{ background:T.greenBg, border:"1px solid #BDE8D2", borderRadius:20, padding:"26px", textAlign:"center" }}>
      <p style={{ fontSize:34, marginBottom:10 }}>✅</p>
      <p style={{ fontWeight:800, fontSize:16, color:"#0B7A55" }}>Message sent!</p>
      <p style={{ fontSize:13, color:T.mut, marginTop:6 }}>We'll get back to you within 24 hours.</p>
      <button className="btn" onClick={()=>setSent(false)} style={{ background:"transparent", color:"#0B7A55", fontSize:13, fontWeight:700, marginTop:10, textDecoration:"underline" }}>Send another message</button>
    </div>
  );

  return (
    <div className="card" style={{ display:"flex", flexDirection:"column", gap:12 }}>
      <p style={{ fontWeight:800, fontSize:15, color:T.ink }}>Send us a message</p>
      <input className="input" placeholder="Subject" value={msg.subject} onChange={e=>setMsg(m=>({...m,subject:e.target.value}))} style={{ fontSize:14 }}/>
      <textarea className="input" placeholder="Describe your issue or question..." value={msg.message} onChange={e=>setMsg(m=>({...m,message:e.target.value}))}
        style={{ fontSize:14, minHeight:110, resize:"vertical", lineHeight:1.6 }}/>
      <button className="btn btn-primary" onClick={doSend} disabled={sending}
        style={{ padding:"15px", fontSize:14 }}>
        {sending ? <><span className="spinner" style={{ borderTopColor:"#fff", borderColor:"rgba(255,255,255,.25)" }}/> Sending...</> : <>Send message →</>}
      </button>
    </div>
  );
}

function XyloProfitLabel(amt) {
  const a = parseFloat(amt) || 0;
  let xyloRate = a < 100 ? 0.011 : a < 500 ? 0.0085 : a < 2000 ? 0.007 : a < 10000 ? 0.006 : 0.005;
  const profit = Math.min(0.20 + a * xyloRate, 300);
  return `₹${profit.toFixed(2)}`;
}

export default function WalletPage() {
  const { profile, logout, refreshProfile, firebaseUser } = useAuth();
  const [screen, setScreen]     = useState("home");
  const [sidebar, setSidebar]   = useState(false);
  const [txs, setTxs]           = useState([]);
  const [notifs, setNotifs]     = useState([]);
  const [toast, setToast]       = useState(null);
  const [loading, setLoading]   = useState(false);

  // PIN state
  const [pinModal, setPinModal]       = useState(null); // null | "verify" | "setup"
  const [pinInput, setPinInput]       = useState("");
  const [pinConfirm, setPinConfirm]   = useState("");
  const [pinStep, setPinStep]         = useState(1); // 1=enter, 2=confirm
  const [pinError, setPinError]       = useState("");
  const [pinCallback, setPinCallback] = useState(null); // function to call after PIN verified
  const [sessionActive, setSessionActive] = useState(true);

  // Send state
  const [send, setSend]         = useState({ recipient:"", amount:"", holdType:"instant", note:"", customMins:"", customHrs:"" });
  const [sendStep, setSendStep] = useState(1);
  const [holdMenu, setHoldMenu] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [recentRecipients, setRecentRecipients] = useState([]);

  // Topup
  const [topup, setTopup] = useState({ amount:"", bank: profile?.bank ? `${profile.bank.name} ••${profile.bank.last4}` : "" });

  // Bank form
  const [bankForm, setBankForm]     = useState({ name:"", routing:"", account:"", confirm:"" });
  const [showBankForm, setShowBankForm] = useState(false);

  // Settings edit
  const [editName, setEditName]   = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editPass, setEditPass]   = useState({ current:"", new:"", confirm:"" });
  const [settingsTab, setSettingsTab] = useState("profile");
  const [showRemoveBankConfirm, setShowRemoveBankConfirm] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null); // for receipt view

  // When navigating to settings, open bank tab if no bank linked
  useEffect(() => {
    if (screen === "settings" && !profile?.bank) {
      setSettingsTab("bank");
    }
  }, [screen]); // profile | security | bank

  const holdRef = useRef(null);
  const sbRef   = useRef(null);

  useEffect(() => {
    if (!profile?.id) return;
    const unsubTx    = subscribeTransactions(profile.id, setTxs);
    const unsubNotif = subscribeNotifications(profile.id, setNotifs);
    return () => { unsubTx(); unsubNotif(); };
  }, [profile?.id]);

  // Load recent recipients from past transactions
  useEffect(() => {
    if (!txs.length) return;
    const seen = new Set();
    const recents = [];
    txs.forEach(tx => {
      if (tx.type !== "credit" && tx.recipient && !seen.has(tx.recipient)) {
        seen.add(tx.recipient);
        recents.push({ email: tx.recipient, name: tx.recipient, avatar: tx.recipient.slice(0,2).toUpperCase() });
      }
    });
    // Enrich with actual user data
    Promise.all(recents.map(async r => {
      try {
        const q = query(collection(db, "users"), where("email","==", r.email.toLowerCase().trim()));
        const snap = await getDocs(q);
        if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };
      } catch(e) {}
      return r;
    })).then(enriched => setRecentRecipients(enriched.slice(0,5)));
  }, [txs]);

  useEffect(() => {
    const fn = e => {
      if (holdRef.current && !holdRef.current.contains(e.target)) setHoldMenu(false);
      if (sbRef.current  && !sbRef.current.contains(e.target))   setSidebar(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const toast$ = (msg, type="success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Session timeout — auto logout after 15 min idle ──────────
  useEffect(() => {
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        toast$("Session expired for security. Please sign in again.", "error");
        setTimeout(() => doLogout(), 2000);
      }, 15 * 60 * 1000); // 15 minutes
    };
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach(e => window.addEventListener(e, reset));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, []);

  // ── PIN Helper — call this before any sensitive action ───────
  // Usage: requirePin(() => doSomething())
  const requirePin = (callback) => {
    if (!hasPin(profile)) {
      // No PIN set yet — prompt setup
      setPinModal("setup");
      setPinStep(1);
      setPinInput("");
      setPinConfirm("");
      setPinError("");
      setPinCallback({ fn: callback }); // ✅ wrap in object to prevent React calling it
    } else {
      // PIN exists — verify it
      setPinModal("verify");
      setPinInput("");
      setPinError("");
      setPinCallback({ fn: callback }); // ✅ wrap in object
    }
  };

  const handlePinSetup = async () => {
    if (pinStep === 1) {
      if (!/^\d{6}$/.test(pinInput)) {
        setPinError("PIN must be exactly 6 digits");
        return;
      }
      setPinConfirm(pinInput);
      setPinInput("");
      setPinStep(2);
      setPinError("");
    } else {
      if (pinInput !== pinConfirm) {
        setPinError("PINs don't match. Try again.");
        setPinInput("");
        setPinStep(1);
        return;
      }
      try {
        await setPin(profile.id, pinInput);
        await refreshProfile();
        setPinModal(null);
        setPinInput("");
        toast$("PIN set successfully! 🔒");
        if (pinCallback?.fn) pinCallback.fn(); // ✅ call via .fn
      } catch(e) {
        setPinError(e.message);
      }
    }
  };

  const handlePinVerify = async () => {
    if (!/^\d{6}$/.test(pinInput)) {
      setPinError("Enter your 6-digit PIN");
      return;
    }
    try {
      const ok = await verifyPin(profile.id, pinInput);
      if (!ok) {
        setPinError("Incorrect PIN. Try again.");
        setPinInput("");
        return;
      }
      setPinModal(null);
      setPinInput("");
      setPinError("");
      if (pinCallback?.fn) pinCallback.fn(); // ✅ call via .fn
    } catch(e) {
      setPinError("Error verifying PIN.");
    }
  };

  // Search registered Xylo users by email or phone
  const searchUsers = async (q) => {
    if (!q || q.length < 3) { setRecipientSearch([]); setSearchLoading(false); return; }
    setSearchLoading(true);
    try {
      const results = [];

      // Search by email
      try {
        const lq = q.toLowerCase().trim();
        const emailQ = query(collection(db, "users"), where("email","==", lq));
        const emailSnap = await getDocs(emailQ);
        emailSnap.forEach(d => {
          if (d.id !== profile.id) results.push({ id:d.id, ...d.data() });
        });
      } catch(e) {}

      // Search by userId
      try {
        const uq = q.toUpperCase().trim();
        const idQ = query(collection(db, "users"), where("userId","==", uq));
        const idSnap = await getDocs(idQ);
        idSnap.forEach(d => {
          if (d.id !== profile.id && !results.find(r=>r.id===d.id))
            results.push({ id:d.id, ...d.data() });
        });
      } catch(e) {}

      // Search by name
      try {
        const allSnap = await getDocs(collection(db, "users"));
        allSnap.forEach(d => {
          if (d.id === profile.id) return;
          const name = (d.data().name || "").toLowerCase();
          if (name.includes(q.toLowerCase().trim()) && !results.find(r=>r.id===d.id))
            results.push({ id:d.id, ...d.data() });
        });
      } catch(e) {}

      setRecipientSearch(results);
    } catch(e) {
      // silent
    } finally {
      setSearchLoading(false);
    }
  };

  // Hold type label — max 48hrs, support minutes too
  const hLabel = () => {
    if (send.holdType === "instant") return "⚡ Instant";
    if (send.holdType === "hold_24") return "⏳ 24 hrs";
    if (send.holdType === "hold_48") return "🔒 48 hrs";
    if (send.holdType === "custom_min") return `⏱ ${send.customMins||"?"}m hold`;
    if (send.holdType === "custom_hr")  return `⏱ ${send.customHrs||"?"}h hold`;
    return "⚡ Instant";
  };

  const getHoldHrs = () => {
    if (send.holdType === "hold_24") return 24;
    if (send.holdType === "hold_48") return 48;
    if (send.holdType === "custom_hr")  return Math.min(parseInt(send.customHrs)||1, 48);
    if (send.holdType === "custom_min") return Math.min(parseInt(send.customMins)||1, 2880) / 60;
    return 0;
  };

  const sAmt = parseFloat(send.amount) || 0;
  const sFee = calcFee(sAmt);
  const unread  = notifs.filter(n => !n.read).length;
  const holds   = txs.filter(t => t.status?.startsWith("hold") && t.releaseAt?.toDate?.()?.getTime() > Date.now());
  const heldBal = holds.reduce((s, t) => s + t.amount, 0);

  // ── Actions ───────────────────────────────────────────────────
  const doTopup = async () => {
    const a = parseFloat(topup.amount);
    if (!a || a <= 0) return toast$("Enter valid amount", "error");
    if (!profile?.bank) return toast$("Link a bank first in Settings", "error");
    setLoading(true);
    try {
      await addMoney(profile.id, a, `${profile.bank.name} ••${profile.bank.last4}`);
      await refreshProfile();
      setTopup(d => ({ ...d, amount:"" }));
      setScreen("home");
      toast$(`₹${a.toFixed(2)} added!`);
    } catch(e) { toast$("Failed. Try again.", "error"); }
    finally { setLoading(false); }
  };

  const doSend = async () => {
    const a = sAmt;
    if (!selectedRecipient && !send.recipient) return toast$("Search and select a recipient", "error");
    if (!a || a <= 0) return toast$("Enter amount", "error");
    if (a + sFee > (profile?.balance || 0)) return toast$("Insufficient balance", "error");
    const holdHrs = getHoldHrs();
    setLoading(true);
    try {
      await sendPayment({
        senderUid:       profile.id,
        senderData:      profile,
        recipientHandle: selectedRecipient ? selectedRecipient.email : send.recipient,
        recipientUid:    selectedRecipient?.id || null,
        amount:          a,
        fee:             sFee,
        holdType:        send.holdType,
        holdHrs,
        note:            send.note,
      });
      await refreshProfile();
      setSend({ recipient:"", amount:"", holdType:"instant", note:"", customMins:"", customHrs:"" });
      setSelectedRecipient(null);
      setRecipientSearch([]);
      setSendStep(1);
      setScreen("home");
      toast$(send.holdType==="instant" ? "Payment sent! ⚡" : "Hold placed. Recipient notified. 🔒");
    } catch(e) {
      console.error("Send error:", e);
      toast$("Payment failed: " + e.message, "error");
    }
    finally { setLoading(false); }
  };

  // PIN-protected send — called from confirm button
  const doSendWithPin = () => {
    const a = sAmt;
    if (!selectedRecipient && !send.recipient) return toast$("Search and select a recipient", "error");
    if (!a || a <= 0) return toast$("Enter amount", "error");
    if (a + sFee > (profile?.balance || 0)) return toast$("Insufficient balance", "error");
    requirePin(doSend);
  };

  const doLinkBank = async () => {
    if (!bankForm.name||!bankForm.routing||!bankForm.account) return toast$("Fill all bank fields","error");
    if (bankForm.account !== bankForm.confirm) return toast$("Account numbers don't match","error");
    setLoading(true);
    try {
      const bank = { name: bankForm.name, last4: bankForm.account.slice(-4) };
      await updateUserProfile(profile.id, { bank });
      await refreshProfile();
      setShowBankForm(false);
      setBankForm({ name:"", routing:"", account:"", confirm:"" });
      toast$("Bank linked! 🏦");
    } catch(e) { toast$("Failed. Try again.", "error"); }
    finally { setLoading(false); }
  };

  const doSaveName = async () => {
    if (!editName.trim()) return toast$("Enter a name","error");
    setLoading(true);
    try {
      await updateUserProfile(profile.id, { name: editName.trim() });
      await refreshProfile();
      setEditName("");
      toast$("Name updated ✓");
    } catch(e) { toast$("Failed","error"); }
    finally { setLoading(false); }
  };

  const doSavePhone = async () => {
    if (!editPhone.trim()) return toast$("Enter phone number","error");
    setLoading(true);
    try {
      await updateUserProfile(profile.id, { phone: editPhone.trim() });
      await refreshProfile();
      setEditPhone("");
      toast$("Phone updated ✓ — verify via SMS");
    } catch(e) { toast$("Failed","error"); }
    finally { setLoading(false); }
  };

  const doSaveEmail = async () => {
    if (!editEmail.includes("@")) return toast$("Enter valid email","error");
    setLoading(true);
    try {
      await updateEmail(firebaseUser, editEmail);
      await sendEmailVerification(firebaseUser);
      await updateUserProfile(profile.id, { email: editEmail });
      await refreshProfile();
      setEditEmail("");
      toast$("Email updated — verification sent!");
    } catch(e) {
      toast$(e.code === "auth/requires-recent-login" ? "Please sign out and sign in again first" : "Failed to update email","error");
    }
    finally { setLoading(false); }
  };

  const doSavePassword = async () => {
    if (!editPass.current) return toast$("Enter current password","error");
    if (editPass.new.length < 6) return toast$("New password must be 6+ characters","error");
    if (editPass.new !== editPass.confirm) return toast$("Passwords don't match","error");
    setLoading(true);
    try {
      const cred = EmailAuthProvider.credential(firebaseUser.email, editPass.current);
      await reauthenticateWithCredential(firebaseUser, cred);
      await updatePassword(firebaseUser, editPass.new);
      setEditPass({ current:"", new:"", confirm:"" });
      toast$("Password updated ✓");
    } catch(e) {
      toast$(e.code === "auth/wrong-password" ? "Current password is incorrect" : "Failed","error");
    }
    finally { setLoading(false); }
  };

  const doMarkRead = async () => {
    const ids = notifs.filter(n=>!n.read).map(n=>n.id);
    if (ids.length) await markAllNotificationsRead(profile.id, ids);
  };

  const doLogout = async () => { await logout(); };

  const Back = ({ to="home" }) => (
    <button className="btn" onClick={()=>{ setScreen(to); setSendStep(1); setSelectedRecipient(null); setRecipientSearch([]); }}
      style={{ background:"#fff", border:`1px solid ${T.line}`, borderRadius:12, width:38, height:38, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, color:T.ink, boxShadow:"0 4px 12px rgba(10,15,30,.07)" }}>
      ←
    </button>
  );

  const renderScreen = () => {

    // ── HOME ─────────────────────────────────────────────────────
    if (screen === "home") return (
      <div className="fade-in">
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"30px 0 18px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:46, height:46, borderRadius:15, background:"linear-gradient(135deg,#0A0F1E,#1B2A5B)", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 8px 20px rgba(10,15,30,.25)" }}>
              <span style={{ fontSize:20, color:"#fff" }}>✦</span>
            </div>
            <div>
              <p style={{ fontSize:12, color:T.faint, fontWeight:600 }}>Good day,</p>
              <p style={{ fontSize:17, fontWeight:800, letterSpacing:-0.4, color:T.ink }}>{profile?.name?.split(" ")[0] || "there"} 👋</p>
            </div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button className="btn" onClick={()=>{ setScreen("notifications"); doMarkRead(); }}
              style={{ position:"relative", background:"#fff", border:`1px solid ${T.line}`, borderRadius:13, width:42, height:42, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, boxShadow:"0 4px 12px rgba(10,15,30,.06)" }}>
              🔔{unread>0&&<span style={{ position:"absolute", top:8, right:8, width:9, height:9, background:T.red, borderRadius:"50%", border:"2px solid #fff" }}/>}
            </button>
            <button className="btn" onClick={()=>setSidebar(true)}
              style={{ background:"#fff", border:`1px solid ${T.line}`, borderRadius:13, width:42, height:42, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:4, boxShadow:"0 4px 12px rgba(10,15,30,.06)" }}>
              {[0,1,2].map(i=><div key={i} style={{ width:16, height:2, background:T.ink, borderRadius:2 }}/>)}
            </button>
          </div>
        </div>

        {/* Balance card */}
        <div className="grad-card" style={{ padding:"24px 22px 20px", marginBottom:18 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <p style={{ fontSize:10, color:"#8FA0C2", letterSpacing:2.2, fontWeight:800 }}>AVAILABLE BALANCE</p>
            <span className="chip">● LIVE</span>
          </div>
          <p style={{ fontSize:46, fontWeight:900, marginTop:10, letterSpacing:-2, color:"#fff" }}>
            <span style={{ fontSize:26, fontWeight:700, color:"#8FA0C2", marginRight:4 }}>₹</span>{(profile?.balance||0).toFixed(2)}
          </p>
          <div style={{ display:"flex", gap:8, marginTop:16, flexWrap:"wrap" }}>
            <div className="chip"><span style={{ color:"#34D399" }}>●</span> {profile?.userId}</div>
            {heldBal>0&&<div className="chip" style={{ color:"#FBBF24" }}>⏳ ₹{heldBal.toFixed(2)} on hold</div>}
            <div className="chip">📊 ₹{Math.max(0, 500 - (profile?.dailySentDate === new Date().toDateString() ? (profile?.dailySent||0) : 0)).toFixed(0)} left today</div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display:"flex", gap:10, marginBottom:22 }}>
          {[
            {l:"Send", s:"send", ic:"↗", bg:"linear-gradient(135deg,#0E9F6E,#34D399)"},
            {l:"Add Money", s:"topup", ic:"+", bg:"linear-gradient(135deg,#2563EB,#60A5FA)"},
            {l:"Activity", s:"activity", ic:"≣", bg:"linear-gradient(135deg,#7C3AED,#A78BFA)"},
          ].map(a=>(
            <button key={a.l} className="btn action-btn" onClick={()=>setScreen(a.s)}>
              <span style={{ width:40, height:40, borderRadius:13, background:a.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, color:"#fff", fontWeight:800, boxShadow:"0 6px 16px rgba(10,15,30,.18)" }}>{a.ic}</span>
              <span style={{ fontSize:12, color:T.mut, fontWeight:700 }}>{a.l}</span>
            </button>
          ))}
        </div>

        {/* Active holds */}
        {holds.length>0&&<div style={{ marginBottom:20 }}>
          <SectionLabel>ACTIVE HOLDS</SectionLabel>
          {holds.map(tx=>(
            <div key={tx.id} style={{ background:"#fff", border:`1px solid #F5D9A8`, borderRadius:20, padding:"16px", marginBottom:10, boxShadow:"0 6px 18px rgba(180,83,9,.08)" }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:10 }}>
                <span style={{ fontSize:13, color:T.mut, fontWeight:600 }}>{tx.label}</span>
                <span style={{ fontSize:14, fontWeight:900, color:T.amber }}>-₹{tx.amount.toFixed(2)}</span>
              </div>
              <div className="hold-bar shimmer" style={{ marginBottom:10 }}/>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span className="tag hold">ON HOLD</span>
                <span className="pulse" style={{ fontSize:11, color:T.amber, fontWeight:700 }}>⏱ {countdown(tx.releaseAt)}</span>
              </div>
            </div>
          ))}
        </div>}

        {/* Recent */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
          <SectionLabel>RECENT ACTIVITY</SectionLabel>
          {txs.length>6&&<button className="btn" onClick={()=>setScreen("activity")} style={{ background:"transparent", fontSize:12, fontWeight:700, color:T.blue }}>View all →</button>}
        </div>
        <div className="card" style={{ padding:"4px 18px", borderRadius:22 }}>
          {txs.length===0
            ? <div style={{ padding:"30px 0", textAlign:"center" }}>
                <p style={{ fontSize:30, marginBottom:10 }}>💸</p>
                <p style={{ fontSize:14, fontWeight:700, color:T.ink }}>No transactions yet</p>
                <p style={{ fontSize:12, color:T.faint, marginTop:4 }}>Add money to send your first payment</p>
              </div>
            : txs.slice(0,6).map(tx=>(
              <div key={tx.id} className="tx-row" onClick={()=>{ setSelectedTx(tx); setScreen("receipt"); }} style={{ cursor:"pointer" }}>
                <IconTile bg={tx.type==="credit"?T.greenBg:tx.type==="hold"?T.amberBg:T.redBg}>{tx.icon}</IconTile>
                <div style={{ flex:1 }}>
                  <p style={{ fontSize:13, fontWeight:700, color:T.ink }}>{tx.label}</p>
                  <p style={{ fontSize:11, color:T.faint, marginTop:2 }}>{timeAgo(tx.createdAt)}</p>
                </div>
                <div style={{ textAlign:"right" }}>
                  <p style={{ fontSize:14, fontWeight:800, color:tx.type==="credit"?T.green:tx.type==="hold"?T.amber:T.red }}>
                    {tx.type==="credit"?"+":"-"}₹{tx.amount.toFixed(2)}
                  </p>
                  <span className={`tag ${tx.type==="credit"||tx.status==="instant"?"done":"hold"}`} style={{ marginTop:4 }}>
                    {tx.status==="completed"?"done":tx.status==="instant"?"sent":tx.status?.includes("24")?"24h":tx.status?.includes("48")?"48h":"hold"}
                  </span>
                </div>
              </div>
            ))
          }
        </div>

        {/* Trust strip */}
        <div style={{ display:"flex", gap:8, marginTop:18, marginBottom:6 }}>
          {[["🔐","PIN protected"],["⚡","Instant"],["📧","Alerts on"]].map(([ic,t])=>(
            <div key={t} style={{ flex:1, background:T.mist, borderRadius:14, padding:"12px 8px", textAlign:"center" }}>
              <p style={{ fontSize:16 }}>{ic}</p>
              <p style={{ fontSize:10, fontWeight:700, color:T.mut, marginTop:4 }}>{t}</p>
            </div>
          ))}
        </div>

        <div style={{ display:"flex", justifyContent:"center", gap:22, marginTop:20, marginBottom:8 }}>
          {[["about","About"],["privacy","Privacy"],["contact","Support"]].map(([s,l])=>(
            <button key={s} className="btn" onClick={()=>setScreen(s)} style={{ background:"transparent", fontSize:12, fontWeight:600, color:T.faint }}>{l}</button>
          ))}
        </div>
      </div>
    );

    // ── SEND ─────────────────────────────────────────────────────
    if (screen === "send") return (
      <div className="fade-in">
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"30px 0 20px" }}>
          <div style={{ display:"flex", gap:12, alignItems:"center" }}>
            <Back/>
            <div><p style={H1}>Send money</p><p style={SUB}>Step {sendStep} of 2 · PIN protected</p></div>
          </div>
          {/* Hold type dropdown */}
          <div ref={holdRef} style={{ position:"relative" }}>
            <button className="btn" onClick={()=>setHoldMenu(o=>!o)}
              style={{ background:"#fff", border:`1px solid ${T.line}`, borderRadius:13, padding:"9px 13px", fontSize:12, color:T.ink, display:"flex", alignItems:"center", gap:6, fontWeight:800, boxShadow:"0 4px 12px rgba(10,15,30,.06)" }}>
              {hLabel()} <span style={{ fontSize:10, opacity:.4 }}>▾</span>
            </button>
            {holdMenu&&(
              <div className="drop-menu">
                {[
                  {k:"instant",  l:"⚡ Instant transfer", s:"Arrives immediately"},
                  {k:"hold_24",  l:"⏳ 24-hour hold",    s:"Locked for 24 hours"},
                  {k:"hold_48",  l:"🔒 48-hour hold",    s:"Locked for 48 hours"},
                  {k:"custom_hr", l:"⏱ Custom hours",   s:"1 to 48 hours max"},
                  {k:"custom_min",l:"⏱ Custom minutes", s:"Up to 48 hours max"},
                ].map(o=>(
                  <button key={o.k} className={`btn drop-item ${send.holdType===o.k?"active":""}`}
                    onClick={()=>{ setSend(d=>({...d,holdType:o.k})); setHoldMenu(false); }}>
                    <div><p style={{ fontWeight:700, fontSize:13, color:T.ink }}>{o.l}</p><p style={{ fontSize:11, color:T.faint, marginTop:2 }}>{o.s}</p></div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {sendStep===1&&(
          <div style={{ display:"flex", flexDirection:"column", gap:13 }}>

            {/* Recipient search */}
            <div>
              <SectionLabel>SEND TO</SectionLabel>
              <div style={{ position:"relative" }}>
                <input className="input" placeholder="Search by name, email or XyloPay ID"
                  value={send.recipient}
                  onChange={e=>{
                    const val = e.target.value;
                    setSend(d=>({...d,recipient:val}));
                    setSelectedRecipient(null);
                    setRecipientSearch([]);
                    if (val.length >= 2) {
                      setSearchLoading(true);
                      clearTimeout(window._xyloSearch);
                      window._xyloSearch = setTimeout(() => searchUsers(val), 400);
                    } else {
                      setSearchLoading(false);
                    }
                  }}/>

                {/* Search results dropdown — shows when typing */}
                {(recipientSearch.length > 0 || (send.recipient.length >= 2 && recentRecipients.length > 0 && !selectedRecipient)) && (
                  <div style={{ position:"absolute", top:"100%", left:0, right:0, background:"#fff", border:`1px solid ${T.line}`, borderRadius:16, marginTop:6, zIndex:200, boxShadow:"0 16px 44px rgba(10,15,30,.14)", overflow:"hidden" }}>
                    {/* Exact search results first */}
                    {recipientSearch.map(u=>(
                      <button key={u.id} className="btn" onClick={()=>{ setSelectedRecipient(u); setSend(d=>({...d,recipient:u.email})); setRecipientSearch([]); }}
                        style={{ width:"100%", background:"transparent", padding:"13px 16px", display:"flex", alignItems:"center", gap:12, borderBottom:`1px solid ${T.line2}`, textAlign:"left" }}>
                        <div style={{ width:38, height:38, background:"linear-gradient(135deg,#0A0F1E,#1B2A5B)", borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:800, fontSize:14, flexShrink:0 }}>{u.avatar}</div>
                        <div>
                          <p style={{ fontWeight:700, fontSize:14, color:T.ink }}>{u.name}</p>
                          <p style={{ fontSize:12, color:T.faint }}>{u.email} · {u.userId}</p>
                          <p style={{ fontSize:11, color:T.green, fontWeight:700, marginTop:2 }}>✓ XyloPay member</p>
                        </div>
                      </button>
                    ))}
                    {/* Recent recipients filtered by what user is typing */}
                    {recentRecipients
                      .filter(r => {
                        const q = send.recipient.toLowerCase();
                        return (r.name||"").toLowerCase().includes(q) || (r.email||"").toLowerCase().includes(q);
                      })
                      .filter(r => !recipientSearch.find(s => s.email === r.email))
                      .map((r, i) => (
                        <button key={`recent-${i}`} className="btn" onClick={()=>{ setSelectedRecipient(r); setSend(d=>({...d,recipient:r.email||""})); setRecipientSearch([]); }}
                          style={{ width:"100%", background:T.mist, padding:"13px 16px", display:"flex", alignItems:"center", gap:12, borderBottom:`1px solid ${T.line2}`, textAlign:"left" }}>
                          <div style={{ width:38, height:38, background:"#334155", borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:800, fontSize:14, flexShrink:0 }}>{r.avatar||"?"}</div>
                          <div>
                            <p style={{ fontWeight:700, fontSize:14, color:T.ink }}>{r.name||r.email}</p>
                            <p style={{ fontSize:12, color:T.faint }}>{r.email}</p>
                            <p style={{ fontSize:11, color:T.mut, marginTop:2 }}>⏱ Recent</p>
                          </div>
                        </button>
                      ))
                    }
                  </div>
                )}
                {searchLoading && <p style={{ fontSize:11, color:T.faint, marginTop:8, paddingLeft:4 }}>Searching XyloPay members...</p>}
                {!searchLoading && send.recipient.length >= 2 && recipientSearch.length === 0 && !selectedRecipient && recentRecipients.filter(r=>(r.name||"").toLowerCase().includes(send.recipient.toLowerCase())||(r.email||"").toLowerCase().includes(send.recipient.toLowerCase())).length === 0 && (
                  <p style={{ fontSize:11, color:T.red, marginTop:8, paddingLeft:4 }}>No XyloPay member found — try their exact email or XyloPay ID</p>
                )}
              </div>
            </div>

            {/* Selected recipient badge */}
            {selectedRecipient&&(
              <div style={{ background:T.greenBg, border:"1px solid #BDE8D2", borderRadius:16, padding:"12px 14px", display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ width:36, height:36, background:"linear-gradient(135deg,#0A0F1E,#1B2A5B)", borderRadius:11, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:800, fontSize:13 }}>{selectedRecipient.avatar}</div>
                <div style={{ flex:1 }}>
                  <p style={{ fontWeight:800, fontSize:14, color:T.ink }}>{selectedRecipient.name}</p>
                  <p style={{ fontSize:11, color:"#0B7A55", fontWeight:600 }}>✓ Verified XyloPay member</p>
                </div>
                <button className="btn" onClick={()=>{ setSelectedRecipient(null); setSend(d=>({...d,recipient:""})); }} style={{ background:"transparent", color:T.faint, fontSize:18, padding:4 }}>×</button>
              </div>
            )}

            {/* Amount */}
            <div>
              <SectionLabel>AMOUNT</SectionLabel>
              <div style={{ position:"relative" }}>
                <span style={{ position:"absolute", left:16, top:"50%", transform:"translateY(-50%)", color:T.ink, fontSize:22, fontWeight:900, pointerEvents:"none" }}>₹</span>
                <input className="input" style={{ paddingLeft:40, fontSize:26, fontWeight:900, color:T.ink }} placeholder="0.00" type="number"
                  value={send.amount} onChange={e=>setSend(d=>({...d,amount:e.target.value}))}/>
              </div>
            </div>

            {/* Custom time inputs */}
            {send.holdType==="custom_hr"&&(
              <div style={{ position:"relative" }}>
                <input className="input" placeholder="Hours (1–48 max)" type="number" min="1" max="48"
                  value={send.customHrs} onChange={e=>setSend(d=>({...d,customHrs:Math.min(48,parseInt(e.target.value)||"")}))}/>
                <span style={{ position:"absolute", right:16, top:"50%", transform:"translateY(-50%)", fontSize:12, color:T.faint, fontWeight:700 }}>HRS</span>
              </div>
            )}
            {send.holdType==="custom_min"&&(
              <div style={{ position:"relative" }}>
                <input className="input" placeholder="Minutes (max 48 hours)" type="number" min="1" max="2880"
                  value={send.customMins} onChange={e=>setSend(d=>({...d,customMins:Math.min(2880,parseInt(e.target.value)||"")}))}/>
                <span style={{ position:"absolute", right:16, top:"50%", transform:"translateY(-50%)", fontSize:12, color:T.faint, fontWeight:700 }}>MIN</span>
              </div>
            )}

            <input className="input" placeholder="Note (optional)" value={send.note} onChange={e=>setSend(d=>({...d,note:e.target.value}))}/>

            {/* Fee preview */}
            {sAmt>0&&(
              <div className="card" style={{ padding:"16px 18px", background:T.mist }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                  <span style={{ fontSize:12, color:T.mut }}>Service fee</span>
                  <span style={{ fontSize:12, color:T.ink, fontWeight:700 }}>₹{sFee.toFixed(2)}</span>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontSize:12, color:T.mut }}>Total from wallet</span>
                  <span style={{ fontSize:16, fontWeight:900, color:T.ink }}>₹{(sAmt+sFee).toFixed(2)}</span>
                </div>
                <div style={{ height:1, background:T.line, margin:"12px 0" }}/>
                <div style={{ display:"flex", justifyContent:"space-between" }}>
                  <span style={{ fontSize:11, color:T.faint }}>Recipient receives</span>
                  <span style={{ fontSize:12, color:T.green, fontWeight:800 }}>₹{sAmt.toFixed(2)}</span>
                </div>
              </div>
            )}

            <button className="btn btn-primary" onClick={()=>{
              if (!selectedRecipient && !send.recipient) return toast$("Search and select a recipient","error");
              if (!send.amount) return toast$("Enter amount","error");
              setSendStep(2);
            }} style={{ padding:"17px", fontSize:15, marginTop:4 }}>
              Review payment →
            </button>
          </div>
        )}

        {sendStep===2&&(
          <div style={{ display:"flex", flexDirection:"column", gap:13 }}>
            <div className="card" style={{ padding:"22px" }}>
              <p className="sec-label">CONFIRM PAYMENT</p>
              <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:20, paddingBottom:18, borderBottom:`1px solid ${T.line2}` }}>
                <div style={{ width:50, height:50, background:"linear-gradient(135deg,#0A0F1E,#1B2A5B)", borderRadius:15, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:800, fontSize:17 }}>
                  {selectedRecipient?.avatar || send.recipient.slice(0,2).toUpperCase()}
                </div>
                <div>
                  <p style={{ fontWeight:800, fontSize:16, color:T.ink }}>{selectedRecipient?.name || send.recipient}</p>
                  <p style={{ fontSize:12, color:T.faint }}>{selectedRecipient?.email || ""}</p>
                </div>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                <span style={{ fontSize:14, color:T.mut }}>Sending</span>
                <span style={{ fontSize:26, fontWeight:900, color:T.ink, letterSpacing:-1 }}>₹{sAmt.toFixed(2)}</span>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                <span style={{ fontSize:12, color:T.faint }}>Service fee</span>
                <span style={{ fontSize:12, color:T.mut, fontWeight:600 }}>₹{sFee.toFixed(2)}</span>
              </div>
              <div style={{ height:1, background:T.line2, margin:"12px 0" }}/>
              <div style={{ display:"flex", justifyContent:"space-between" }}>
                <span style={{ fontSize:13, color:T.mut, fontWeight:700 }}>Total from your wallet</span>
                <span style={{ fontSize:14, fontWeight:900, color:T.ink }}>₹{(sAmt+sFee).toFixed(2)}</span>
              </div>
              {send.holdType!=="instant"&&(
                <div style={{ marginTop:16, background:T.amberBg, border:"1px solid #F5D9A8", borderRadius:14, padding:"12px 14px" }}>
                  <p style={{ fontSize:12, color:T.amber, fontWeight:600, lineHeight:1.6 }}>⏳ {hLabel()} — the recipient is notified now but receives funds after the hold expires. You can cancel anytime before release. Max hold: 48 hours.</p>
                </div>
              )}
            </div>

            <div style={{ display:"flex", gap:10 }}>
              <button className="btn btn-ghost" onClick={()=>setSendStep(1)}
                style={{ padding:"15px", fontSize:13, flex:1 }}>Back</button>
              <button className="btn btn-primary" onClick={doSendWithPin} disabled={loading}
                style={{ padding:"15px", fontSize:14, flex:2 }}>
                {loading&&<span className="spinner" style={{ borderTopColor:"#fff", borderColor:"rgba(255,255,255,.25)" }}/>}
                {loading ? "Processing..." : send.holdType==="instant" ? "Send now ⚡" : "Place hold 🔒"}
              </button>
            </div>
            <p style={{ textAlign:"center", fontSize:11, color:T.faint }}>🔐 Protected by your 6-digit PIN</p>
          </div>
        )}
      </div>
    );

    // ── ADD MONEY ─────────────────────────────────────────────────
    if (screen==="topup") return (
      <div className="fade-in">
        <div style={{ display:"flex", gap:12, alignItems:"center", padding:"30px 0 20px" }}><Back/><div><p style={H1}>Add money</p><p style={SUB}>Top up from your bank</p></div></div>
        {!profile?.bank?(
          <div className="card" style={{ padding:"34px 26px", textAlign:"center", marginBottom:16 }}>
            <div style={{ width:64, height:64, borderRadius:20, background:T.mist, display:"flex", alignItems:"center", justifyContent:"center", fontSize:30, margin:"0 auto 16px" }}>🏦</div>
            <p style={{ fontWeight:800, fontSize:17, marginBottom:8, color:T.ink }}>No bank linked yet</p>
            <p style={{ fontSize:13, color:T.mut, marginBottom:22, lineHeight:1.6 }}>Link your bank account in Settings to add money to your wallet.</p>
            <button className="btn btn-primary" onClick={()=>{ setScreen("settings"); }} style={{ padding:"14px 30px", fontSize:14 }}>Link bank account →</button>
          </div>
        ):(
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div className="card" style={{ padding:"14px 16px", display:"flex", alignItems:"center", gap:12 }}>
              <IconTile bg={T.greenBg}>🏦</IconTile>
              <div><p style={{ fontSize:14, fontWeight:800, color:T.ink }}>{profile.bank.name}</p><p style={{ fontSize:11, color:T.faint, fontFamily:"'JetBrains Mono',monospace" }}>•••• {profile.bank.last4}</p></div>
              <span className="tag done" style={{ marginLeft:"auto" }}>VERIFIED</span>
            </div>
            <div>
              <SectionLabel>AMOUNT</SectionLabel>
              <div style={{ position:"relative" }}>
                <span style={{ position:"absolute", left:16, top:"50%", transform:"translateY(-50%)", color:T.ink, fontSize:24, fontWeight:900, pointerEvents:"none" }}>₹</span>
                <input className="input" style={{ paddingLeft:42, fontSize:30, fontWeight:900, color:T.ink }} placeholder="0.00" type="number"
                  value={topup.amount} onChange={e=>setTopup(d=>({...d,amount:e.target.value}))}/>
              </div>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              {[500,1000,2500,5000].map(a=>(
                <button key={a} className="btn" onClick={()=>setTopup(d=>({...d,amount:a}))}
                  style={{ flex:1, background:topup.amount==a?T.ink:"#fff", border:`1.5px solid ${topup.amount==a?T.ink:T.line}`, borderRadius:14, padding:"13px 0", fontSize:14, color:topup.amount==a?"#fff":T.mut, fontWeight:800 }}>
                  ₹{a.toLocaleString("en-IN")}
                </button>
              ))}
            </div>
            <button className="btn btn-primary" onClick={doTopup} disabled={loading} style={{ padding:"17px", fontSize:15 }}>
              {loading&&<span className="spinner" style={{ borderTopColor:"#fff", borderColor:"rgba(255,255,255,.25)" }}/>}
              {loading ? "Adding..." : `Add ₹${parseFloat(topup.amount||0).toFixed(2)} to wallet`}
            </button>
            <p style={{ textAlign:"center", fontSize:11, color:T.faint }}>Money arrives instantly · No top-up fee</p>
          </div>
        )}
      </div>
    );

    // ── RECEIPT ───────────────────────────────────────────────────
    if (screen==="receipt" && selectedTx) return (
      <div className="fade-in">
        <div style={{ display:"flex", gap:12, alignItems:"center", padding:"30px 0 20px" }}>
          <button className="btn" onClick={()=>{ setScreen("activity"); }}
            style={{ background:"#fff", border:`1px solid ${T.line}`, borderRadius:12, width:38, height:38, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, color:T.ink, boxShadow:"0 4px 12px rgba(10,15,30,.07)" }}>←</button>
          <p style={H1}>Receipt</p>
        </div>

        <div style={{ textAlign:"center", padding:"30px 0 34px", borderBottom:`1px solid ${T.line2}`, marginBottom:22 }}>
          <div style={{ width:68, height:68, background:selectedTx.type==="credit"?T.greenBg:selectedTx.type==="hold"?T.amberBg:T.mist, borderRadius:22, display:"flex", alignItems:"center", justifyContent:"center", fontSize:30, margin:"0 auto 18px" }}>
            {selectedTx.type==="credit" ? "💰" : selectedTx.type==="hold" ? "⏳" : "💸"}
          </div>
          <p style={{ fontSize:42, fontWeight:900, letterSpacing:-1.5, color:selectedTx.type==="credit"?T.green:T.ink }}>
            {selectedTx.type==="credit"?"+":"-"}₹{selectedTx.amount?.toFixed(2)}
          </p>
          <p style={{ fontSize:13, color:T.mut, marginTop:8 }}>{selectedTx.label}</p>
          <span className={`tag ${selectedTx.type==="credit"?"done":"hold"}`} style={{ marginTop:12 }}>
            {selectedTx.status==="completed"?"COMPLETED":selectedTx.status==="instant"?"SENT":selectedTx.type==="hold"?"ON HOLD":"DONE"}
          </span>
        </div>

        <div className="card" style={{ padding:"6px 18px", marginBottom:20 }}>
          {[
            selectedTx.fee>0 && ["Fee", `₹${selectedTx.fee?.toFixed(2)}`],
            selectedTx.recipient && ["To", selectedTx.recipient],
            selectedTx.note && ["Note", selectedTx.note],
            selectedTx.txDate && ["Date", selectedTx.txDate],
            selectedTx.txTime && ["Time", selectedTx.txTime],
            ["Status", selectedTx.status==="completed"?"Completed":selectedTx.status==="instant"?"Sent":selectedTx.type==="hold"?"On hold":"Done"],
            selectedTx.releaseAt && selectedTx.releaseAt.toDate?.()?.getTime()>Date.now() && ["Releases in", countdown(selectedTx.releaseAt)],
          ].filter(Boolean).map(([label, value], i, arr) => (
            <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"15px 0", borderBottom: i<arr.length-1?`1px solid ${T.line2}`:"none" }}>
              <span style={{ fontSize:13, color:T.faint }}>{label}</span>
              <span style={{ fontSize:13, color:T.ink, fontWeight:700, maxWidth:"60%", textAlign:"right" }}>{value}</span>
            </div>
          ))}
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {selectedTx.type !== "credit" && selectedTx.recipient && (
            <button className="btn btn-primary" onClick={()=>{
              setSend(d=>({ ...d, recipient: selectedTx.recipient, amount:"", note:"" }));
              setSelectedRecipient(null);
              setSendStep(1);
              setScreen("send");
            }} style={{ padding:"16px", fontSize:14 }}>
              ↗ Send again to {selectedTx.recipient?.split("@")[0]}
            </button>
          )}
          <button className="btn btn-ghost" onClick={()=>setScreen("activity")} style={{ padding:"15px", fontSize:14 }}>
            ← Back to activity
          </button>
        </div>
      </div>
    );

    // ── ACTIVITY ──────────────────────────────────────────────────
    if (screen==="activity") return (
      <div className="fade-in">
        <div style={{ display:"flex", gap:12, alignItems:"center", padding:"30px 0 20px" }}><Back/><div><p style={H1}>Activity</p><p style={SUB}>Every rupee, accounted for</p></div></div>

        <div className="card" style={{ padding:"4px 18px" }}>
          {txs.length===0?<div style={{ padding:"34px 0", textAlign:"center" }}>
              <p style={{ fontSize:30, marginBottom:10 }}>📭</p>
              <p style={{ fontSize:14, fontWeight:700, color:T.ink }}>Nothing here yet</p>
              <p style={{ fontSize:12, color:T.faint, marginTop:4 }}>Your transactions will appear here</p>
            </div>:
            txs.map(tx=>(
              <div key={tx.id} className="tx-row" onClick={()=>{ setSelectedTx(tx); setScreen("receipt"); }} style={{ cursor:"pointer" }}>
                <IconTile bg={tx.type==="credit"?T.greenBg:tx.type==="hold"?T.amberBg:"#FDECEC"}>{tx.icon}</IconTile>
                <div style={{ flex:1 }}>
                  <p style={{ fontSize:13, fontWeight:700, color:T.ink }}>{tx.label}</p>
                  <p style={{ fontSize:11, color:T.faint, marginTop:2 }}>{tx.txDate||""} {tx.txTime||""} {!tx.txDate&&timeAgo(tx.createdAt)}</p>
                  {tx.releaseAt&&tx.releaseAt.toDate?.()?.getTime()>Date.now()&&<p className="pulse" style={{ fontSize:11, color:T.amber, fontWeight:700, marginTop:3 }}>⏳ Releases in {countdown(tx.releaseAt)}</p>}
                </div>
                <div style={{ textAlign:"right" }}>
                  <p style={{ fontSize:14, fontWeight:800, color:tx.type==="credit"?T.green:tx.type==="hold"?T.amber:T.red }}>{tx.type==="credit"?"+":"-"}₹{tx.amount.toFixed(2)}</p>
                  <span className={`tag ${tx.type==="credit"||tx.status==="instant"?"done":"hold"}`} style={{ marginTop:4 }}>{tx.status==="completed"?"done":tx.status==="instant"?"sent":tx.status?.includes("24")?"24h":"hold"}</span>
                </div>
              </div>
            ))
          }
        </div>
      </div>
    );

    // ── NOTIFICATIONS ─────────────────────────────────────────────
    if (screen==="notifications") return (
      <div className="fade-in">
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"30px 0 20px" }}>
          <div style={{ display:"flex", gap:12, alignItems:"center" }}><Back/><p style={H1}>Alerts</p></div>
          {unread>0&&<button className="btn" onClick={doMarkRead} style={{ background:"transparent", fontSize:12, color:T.ink, fontWeight:800, textDecoration:"underline" }}>Mark all read</button>}
        </div>
        {notifs.length===0?<div style={{ textAlign:"center", padding:"60px 0" }}>
            <p style={{ fontSize:34, marginBottom:12 }}>🔕</p>
            <p style={{ fontSize:14, fontWeight:700, color:T.ink }}>All caught up</p>
            <p style={{ fontSize:12, color:T.faint, marginTop:4 }}>Payment alerts will show up here</p>
          </div>:
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {notifs.map(n=>(
              <div key={n.id} style={{ backgroundColor:n.read?"#fff":T.mist, border:`1px solid ${n.read?T.line2:"#D7DEEB"}`, borderRadius:18, padding:"15px 16px", display:"flex", gap:13, boxShadow:n.read?"none":"0 6px 18px rgba(10,15,30,.07)" }}>
                <IconTile size={40} bg={n.forStranger?T.amberBg:"#EAF1FE"}>{n.forStranger?"⏳":"🔔"}</IconTile>
                <div style={{ flex:1 }}>
                  <p style={{ fontSize:13, lineHeight:1.55, color:n.read?T.mut:T.ink, fontWeight:n.read?400:600 }}>{n.text}</p>
                  <p style={{ fontSize:11, color:T.faint, marginTop:5 }}>{timeAgo(n.createdAt)}</p>
                </div>
                {!n.read&&<div style={{ width:9, height:9, background:T.blue, borderRadius:"50%", flexShrink:0, marginTop:6 }}/>}
              </div>
            ))}
          </div>
        }
      </div>
    );

    // ── PROFILE ───────────────────────────────────────────────────
    if (screen==="profile") return (
      <div className="fade-in">
        <div style={{ display:"flex", gap:12, alignItems:"center", padding:"30px 0 18px" }}>
          <Back/><p style={H1}>Profile</p>
        </div>

        {/* Profile Card */}
        <div className="card" style={{ display:"flex", gap:15, alignItems:"center", marginBottom:14, padding:22 }}>
          {profile?.photoURL
            ?<img src={profile.photoURL} alt="" style={{ width:58, height:58, borderRadius:19, objectFit:"cover" }}/>
            :<div style={{ width:58, height:58, background:"linear-gradient(135deg,#0A0F1E,#1B2A5B)", borderRadius:19, display:"flex", alignItems:"center", justifyContent:"center", fontSize:23, fontWeight:900, color:"#fff" }}>{profile?.avatar}</div>
          }
          <div>
            <p style={{ fontWeight:800, fontSize:18, color:T.ink, letterSpacing:-0.3 }}>{profile?.name}</p>
            <p style={{ fontSize:13, color:T.mut, marginTop:3 }}>{profile?.email}</p>
            {profile?.phone&&<p style={{ fontSize:12, color:T.faint, marginTop:3 }}>📱 {profile.phone}</p>}
            <p style={{ fontSize:10, color:T.faint, fontFamily:"'JetBrains Mono',monospace", marginTop:4, letterSpacing:.5 }}>{profile?.userId}</p>
          </div>
        </div>

        {/* Edit Name */}
        <div className="card" style={{ marginBottom:12 }}>
          <p style={{ fontWeight:800, fontSize:13, color:T.ink, marginBottom:10 }}>Display name</p>
          <div style={{ display:"flex", gap:8 }}>
            <input className="input" placeholder={profile?.name||"Your name"} value={editName} onChange={e=>setEditName(e.target.value)} style={{ flex:1, fontSize:14 }}/>
            <button className="btn btn-primary" onClick={doSaveName} disabled={loading} style={{ padding:"0 20px", fontSize:13, borderRadius:14 }}>Save</button>
          </div>
        </div>

        {/* Edit Phone */}
        <div className="card" style={{ marginBottom:12 }}>
          <p style={{ fontWeight:800, fontSize:13, color:T.ink, marginBottom:4 }}>Phone number</p>
          <p style={{ fontSize:11, color:T.faint, marginBottom:10 }}>For security & verification only</p>
          <div style={{ display:"flex", gap:8 }}>
            <input className="input" placeholder="+91 98765 43210" value={editPhone} onChange={e=>setEditPhone(e.target.value)} style={{ flex:1, fontSize:14 }} type="tel"/>
            <button className="btn btn-primary" onClick={doSavePhone} disabled={loading} style={{ padding:"0 20px", fontSize:13, borderRadius:14 }}>Save</button>
          </div>
          {profile?.phone&&<p style={{ fontSize:11, color:T.green, fontWeight:600, marginTop:10 }}>✓ Current: {profile.phone}</p>}
        </div>

        {/* Xylo ID */}
        <div className="card" style={{ marginBottom:12, background:"linear-gradient(135deg,#0A0F1E,#16213C)", border:"none" }}>
          <p style={{ fontWeight:800, fontSize:13, color:"#fff", marginBottom:4 }}>Your XyloPay ID</p>
          <p style={{ fontSize:11, color:"#8FA0C2", marginBottom:12 }}>Share it so anyone can send you money</p>
          <div style={{ background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.12)", borderRadius:14, padding:"13px 14px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <p style={{ fontSize:15, fontWeight:800, color:"#fff", fontFamily:"'JetBrains Mono',monospace", letterSpacing:1 }}>{profile?.userId}</p>
              <p style={{ fontSize:12, color:"#8FA0C2", marginTop:3 }}>{profile?.email}</p>
            </div>
            <button className="btn" onClick={()=>{ navigator.clipboard?.writeText(profile?.userId); toast$("Copied ✓"); }}
              style={{ background:"#fff", color:T.ink, borderRadius:11, padding:"9px 16px", fontSize:12, fontWeight:800 }}>Copy</button>
          </div>
        </div>

        <button className="btn" onClick={doLogout}
          style={{ background:T.redBg, border:"1px solid #F6C9C9", borderRadius:16, padding:"15px", fontSize:14, color:T.red, fontWeight:800, width:"100%", marginTop:6 }}>
          Sign out
        </button>
      </div>
    );

    // ── SETTINGS ──────────────────────────────────────────────────
    if (screen==="settings") return (
      <div className="fade-in">
        <div style={{ display:"flex", gap:12, alignItems:"center", padding:"30px 0 18px" }}>
          <Back/><p style={H1}>Settings</p>
        </div>

        {/* Bank */}
        <div className="card" style={{ marginBottom:12 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:6 }}>
            <IconTile bg={T.greenBg}>🏦</IconTile>
            <div>
              <p style={{ fontWeight:800, fontSize:15, color:T.ink }}>Linked bank</p>
              <p style={{ fontSize:12, color:T.faint }}>Connect securely to add money</p>
            </div>
          </div>
          {profile?.bank?(
            <div style={{ marginTop:12 }}>
              <div style={{ display:"flex", gap:12, alignItems:"center", marginBottom:14, background:T.mist, borderRadius:14, padding:"13px 14px" }}>
                <div>
                  <p style={{ fontSize:14, fontWeight:800, color:T.ink }}>{profile.bank.name}</p>
                  <p style={{ fontSize:12, color:T.faint, fontFamily:"'JetBrains Mono',monospace" }}>•••• {profile.bank.last4} · {profile.bank.type||"Account"}</p>
                  <p style={{ fontSize:11, color:T.green, fontWeight:700, marginTop:3 }}>✓ Verified & secure</p>
                </div>
              </div>
              {!showRemoveBankConfirm ? (
                <button className="btn" onClick={()=>setShowRemoveBankConfirm(true)}
                  style={{ fontSize:12, fontWeight:700, color:T.red, background:"transparent", padding:"4px 0" }}>Remove bank</button>
              ) : (
                <div style={{ background:T.redBg, border:"1px solid #F6C9C9", borderRadius:14, padding:"15px" }}>
                  <p style={{ fontWeight:800, fontSize:13, color:T.ink, marginBottom:6 }}>Remove {profile.bank.name}?</p>
                  <p style={{ fontSize:12, color:T.mut, marginBottom:12 }}>You won't be able to add money until you link a new bank.</p>
                  <div style={{ display:"flex", gap:8 }}>
                    <button className="btn btn-ghost" onClick={()=>setShowRemoveBankConfirm(false)} style={{ flex:1, padding:"11px", fontSize:13 }}>Cancel</button>
                    <button className="btn" onClick={async()=>{
                      setLoading(true);
                      try {
                        await updateUserProfile(profile.id, { bank:null, plaidAccessToken:null });
                        await refreshProfile();
                        setShowRemoveBankConfirm(false);
                        toast$("Bank removed");
                      } catch(e) { toast$("Failed","error"); }
                      finally { setLoading(false); }
                    }} disabled={loading}
                      style={{ flex:1, background:T.red, borderRadius:999, padding:"11px", fontSize:13, fontWeight:800, color:"#fff" }}>
                      {loading?"Removing...":"Yes, remove"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ):(
            <div style={{ marginTop:12 }}><PlaidLinkButton profile={profile} refreshProfile={refreshProfile} toast$={toast$} updateUserProfile={updateUserProfile}/></div>
          )}
        </div>

        {/* Password — email users only */}
        {profile?.loginMethod!=="google"&&(
          <div className="card" style={{ marginBottom:12 }}>
            <p style={{ fontWeight:800, fontSize:14, color:T.ink, marginBottom:12 }}>Change password</p>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <input className="input" placeholder="Current password" type="password" value={editPass.current} onChange={e=>setEditPass(p=>({...p,current:e.target.value}))} style={{ fontSize:14 }}/>
              <input className="input" placeholder="New password (6+ characters)" type="password" value={editPass.new} onChange={e=>setEditPass(p=>({...p,new:e.target.value}))} style={{ fontSize:14 }}/>
              <input className="input" placeholder="Confirm new password" type="password" value={editPass.confirm} onChange={e=>setEditPass(p=>({...p,confirm:e.target.value}))} style={{ fontSize:14 }}/>
              <button className="btn btn-primary" onClick={doSavePassword} disabled={loading} style={{ padding:"14px", fontSize:13 }}>Update password</button>
            </div>
          </div>
        )}
        {profile?.loginMethod==="google"&&(
          <div className="card" style={{ marginBottom:12, background:T.mist }}>
            <p style={{ fontSize:13, color:T.mut }}>🔒 Signed in with Google — your password is managed by Google.</p>
          </div>
        )}

        {/* PIN Management */}
        <div className="card" style={{ marginBottom:12 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:10 }}>
            <IconTile bg={hasPin(profile)?T.greenBg:T.amberBg}>🔐</IconTile>
            <div>
              <p style={{ fontWeight:800, fontSize:14, color:T.ink }}>Transaction PIN</p>
              <p style={{ fontSize:12, color:hasPin(profile)?T.green:T.amber, fontWeight:600, marginTop:2 }}>
                {hasPin(profile) ? "Active — required before every payment" : "Not set — payments aren't PIN protected"}
              </p>
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => {
            setPinModal("setup");
            setPinStep(1);
            setPinInput("");
            setPinConfirm("");
            setPinError("");
            setPinCallback(null);
          }} style={{ padding:"14px", fontSize:13, width:"100%" }}>
            {hasPin(profile) ? "Change PIN" : "Set up PIN"}
          </button>
        </div>

        {/* Daily Limit Info */}
        <div className="card" style={{ marginBottom:12 }}>
          <p style={{ fontWeight:800, fontSize:14, color:T.ink, marginBottom:12 }}>Daily sending limit</p>
          {[
            ["Daily limit", "₹500.00", T.ink],
            ["Sent today", `₹${(profile?.dailySentDate === new Date().toDateString() ? (profile?.dailySent||0) : 0).toFixed(2)}`, T.amber],
            ["Remaining today", `₹${Math.max(0, 500 - (profile?.dailySentDate === new Date().toDateString() ? (profile?.dailySent||0) : 0)).toFixed(2)}`, T.green],
          ].map(([l,v,c])=>(
            <div key={l} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0" }}>
              <p style={{ fontSize:13, color:T.mut }}>{l}</p>
              <p style={{ fontSize:14, fontWeight:800, color:c }}>{v}</p>
            </div>
          ))}
          <div style={{ background:T.mist, borderRadius:12, padding:"11px 14px", marginTop:10 }}>
            <p style={{ fontSize:11, color:T.mut, lineHeight:1.6 }}>Limit resets at midnight. Max ₹250 per transaction · max 5 transactions per hour.</p>
          </div>
        </div>
      </div>
    );

    // ── ABOUT ─────────────────────────────────────────────────────
    if (screen==="about") return (
      <div className="fade-in">
        <div style={{ display:"flex", gap:12, alignItems:"center", padding:"30px 0 20px" }}><Back/><p style={H1}>About</p></div>
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <div className="grad-card" style={{ textAlign:"center", padding:"34px 20px" }}>
            <div style={{ width:64, height:64, background:"rgba(255,255,255,.1)", border:"1px solid rgba(255,255,255,.16)", borderRadius:20, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}><span style={{ fontSize:28, color:"#fff" }}>✦</span></div>
            <p style={{ fontWeight:900, fontSize:30, letterSpacing:-1.2, color:"#fff" }}>xylo<span style={{ color:"#7EE2B8", fontWeight:600 }}>pay</span></p>
            <p style={{ fontSize:12, color:"#8FA0C2", marginTop:6, letterSpacing:1 }}>SECURE DIGITAL WALLET · INDIA</p>
          </div>

          <div className="card">
            <p style={{ fontWeight:800, fontSize:15, color:T.ink, marginBottom:6 }}>What is XyloPay?</p>
            <p style={{ fontSize:13, color:T.mut, lineHeight:1.75 }}>XyloPay is a secure digital wallet for India. Send money instantly, or place it on hold — giving both sender and receiver complete control and protection on every payment.</p>
          </div>

          {[
            { ic:"👤", t:"Create your account", d:"Sign up with Google or email in under a minute. Free forever." },
            { ic:"🏦", t:"Add money", d:"Link your bank in Settings and top up your wallet securely." },
            { ic:"⚡", t:"Send instantly", d:"Find any XyloPay member by name, email, or ID — money arrives in seconds." },
            { ic:"⏳", t:"Hold for protection", d:"Lock funds up to 48 hours. The recipient is notified now, receives later — cancel anytime before release." },
            { ic:"🔐", t:"Stay protected", d:"Every payment needs your 6-digit PIN. Email alerts keep you posted on everything." },
          ].map((item,i)=>(
            <div key={i} className="card" style={{ display:"flex", gap:14, alignItems:"flex-start" }}>
              <IconTile bg={T.mist}>{item.ic}</IconTile>
              <div>
                <p style={{ fontWeight:800, fontSize:14, color:T.ink, marginBottom:4 }}>{item.t}</p>
                <p style={{ fontSize:13, color:T.mut, lineHeight:1.65 }}>{item.d}</p>
              </div>
            </div>
          ))}

          <div className="card" style={{ background:T.mist, textAlign:"center" }}>
            <p style={{ fontSize:12, color:T.faint }}>Questions? We reply within 24 hours</p>
            <p style={{ fontWeight:800, fontSize:15, color:T.ink, marginTop:6 }}>ask999k@gmail.com</p>
          </div>
        </div>
      </div>
    );

    // ── PRIVACY ───────────────────────────────────────────────────
    if (screen==="privacy") return (
      <div className="fade-in">
        <div style={{ display:"flex", gap:12, alignItems:"center", padding:"30px 0 20px" }}><Back/><p style={H1}>Privacy</p></div>
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {[["What we collect","Name, email, phone, tokenized bank details, and transaction history — used only to run your wallet. Never sold, never shared for marketing."],["How we protect it","Bank-grade encryption in transit and at rest. Raw bank account numbers are never stored on our servers."],["Hold payments","Recipients are notified the moment a hold is placed. Locked funds can't move until the timer ends or you release them."],["Your rights","Email ask999k@gmail.com to access, correct, or delete your data. We respond within 30 days."]].map(([t,b])=>(
            <div key={t} className="card"><p style={{ fontWeight:800, fontSize:14, color:T.ink, marginBottom:8 }}>{t}</p><p style={{ fontSize:13, color:T.mut, lineHeight:1.75 }}>{b}</p></div>
          ))}
        </div>
      </div>
    );

    // ── CONTACT ───────────────────────────────────────────────────
    if (screen==="contact") return (
      <div className="fade-in">
        <div style={{ display:"flex", gap:12, alignItems:"center", padding:"30px 0 20px" }}><Back/><p style={H1}>Support</p></div>
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div style={{ textAlign:"center", padding:"10px 0 4px" }}>
            <div style={{ width:64, height:64, borderRadius:20, background:"linear-gradient(135deg,#0E9F6E,#34D399)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, margin:"0 auto 14px", boxShadow:"0 12px 28px rgba(14,159,110,.35)" }}>💬</div>
            <p style={{ fontWeight:900, fontSize:20, letterSpacing:-0.5, color:T.ink }}>We're here to help</p>
            <p style={{ fontSize:13, color:T.mut, marginTop:6 }}>Message us — we reply within 24 hours</p>
          </div>

          <ContactForm profile={profile} toast$={toast$}/>

          <div className="card" style={{ display:"flex", gap:14, alignItems:"center" }}>
            <IconTile bg={T.mist}>✉️</IconTile>
            <div>
              <p style={{ fontSize:12, color:T.faint }}>Prefer email?</p>
              <p style={{ fontSize:15, fontWeight:800, color:T.ink, marginTop:3 }}>ask999k@gmail.com</p>
            </div>
          </div>
        </div>
      </div>
    );

    return null;
  };

  return (
    <div style={{ maxWidth:480, margin:"0 auto", padding:"0 18px calc(84px + env(safe-area-inset-bottom))" }}>
      {renderScreen()}

      {/* Sidebar Overlay */}
      {sidebar&&(
        <div className="sidebar-backdrop" onClick={()=>setSidebar(false)}>
          <div className="sidebar" onClick={e=>e.stopPropagation()}>
            <div className="sidebar-hdr">
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                {profile?.photoURL
                  ?<img src={profile.photoURL} alt="" style={{ width:46, height:46, borderRadius:15, objectFit:"cover" }}/>
                  :<div style={{ width:46, height:46, background:"linear-gradient(135deg,#0A0F1E,#1B2A5B)", borderRadius:15, display:"flex", alignItems:"center", justifyContent:"center", fontSize:19, fontWeight:900, color:"#fff" }}>{profile?.avatar}</div>}
                <div>
                  <p style={{ fontWeight:900, fontSize:15, color:T.ink }}>{profile?.name}</p>
                  <p style={{ fontSize:11, color:T.faint }}>{profile?.email}</p>
                </div>
              </div>
              <button className="btn" onClick={()=>setSidebar(false)} style={{ background:"transparent", fontSize:20, color:T.faint, padding:6 }}>×</button>
            </div>
            <div className="sidebar-body">
              <div className="nav-sec">WALLET</div>
              {[
                {l:"Home",s:"home",ic:"🏠"},
                {l:"Send money",s:"send",ic:"↗"},
                {l:"Add money",s:"topup",ic:"+"},
                {l:"Activity",s:"activity",ic:"≣"},
                {l:"Alerts",s:"notifications",ic:"🔔",badge:unread},
              ].map(i=>(
                <button key={i.l} className={`btn nav ${screen===i.s?"on":""}`}
                  onClick={()=>{ setScreen(i.s); setSidebar(false); if(i.s==="notifications")doMarkRead(); }}>
                  <span style={{ width:26, textAlign:"center", fontSize:15 }}>{i.ic}</span>{i.l}
                  {i.badge>0&&<span style={{ marginLeft:"auto", background:T.red, color:"#fff", fontSize:10, fontWeight:800, borderRadius:10, padding:"2px 8px" }}>{i.badge}</span>}
                </button>
              ))}
              <div className="nav-sec">ACCOUNT</div>
              {[
                {l:"Profile",s:"profile",ic:"👤"},
                {l:"Settings",s:"settings",ic:"⚙️"},
              ].map(i=>(
                <button key={i.l} className={`btn nav ${screen===i.s?"on":""}`}
                  onClick={()=>{ setScreen(i.s); setSidebar(false); }}>
                  <span style={{ width:26, textAlign:"center", fontSize:15 }}>{i.ic}</span>{i.l}
                </button>
              ))}
            </div>
            <div style={{ marginTop:"auto" }}>
              <div className="divider"/>
              {[
                {l:"About",s:"about"},{l:"Privacy",s:"privacy"},{l:"Support",s:"contact"},
              ].map(i=>(
                <button key={i.l} className="btn nav" onClick={()=>{ setScreen(i.s); setSidebar(false); }}>{i.l}</button>
              ))}
              <div className="divider"/>
              <button className="btn" onClick={doLogout} style={{ width:"100%", padding:"12px 16px", background:T.redBg, color:T.red, fontWeight:800, fontSize:14, textAlign:"center", borderRadius:14, cursor:"pointer", border:"1px solid #F6C9C9" }}>Sign out</button>
              <p style={{ fontSize:11, color:T.faint, marginTop:12, textAlign:"center" }}>XyloPay v2.1 · Made with ✦</p>
            </div>
          </div>
        </div>
      )}

      {/* PIN Modal — original keypad logic, premium skin */}
      {pinModal&&(
        <div style={{ position:"fixed", inset:0, zIndex:1001, display:"flex", alignItems:"flex-end", justifyContent:"center", background:"rgba(5,8,16,.62)", backdropFilter:"blur(6px)" }} onClick={()=>setPinModal(null)}>
          <div className="pin-card" onClick={e=>e.stopPropagation()}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
              <p style={{ fontWeight:900, fontSize:18, letterSpacing:-0.4, color:T.ink }}>
                {pinModal==="setup" ? (pinStep===1?"Create your PIN":"Confirm your PIN") : "Enter your PIN"}
              </p>
              <button className="btn" onClick={()=>setPinModal(null)} style={{ background:"transparent", fontSize:20, color:T.faint, padding:4 }}>×</button>
            </div>
            <p style={{ fontSize:13, color:T.faint, marginBottom:20 }}>
              {pinModal==="setup" ? (pinStep===1?"6 digits to protect your wallet":"Re-enter to confirm") : "Required before sending money"}
            </p>
            <div style={{ display:"flex", gap:12, justifyContent:"center", marginBottom:22 }}>
              {(pinStep===1?pinInput:pinConfirm).split("").map((d,i)=>(
                <div key={i} className="pin-dot filled">{d}</div>
              ))}
              {Array.from({ length:6-(pinStep===1?pinInput:pinConfirm).length }).map((_,i)=>(<div key={"e"+i} className="pin-dot"/>))}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:10, maxWidth:260, margin:"0 auto 16px" }}>
              {[1,2,3,4,5,6,7,8,9].map(d=>(
                <button key={d} className="btn pin-btn" onClick={()=>{
                  if (pinStep===1) { if (pinInput.length<6) setPinInput(pinInput+d); }
                  else { if (pinConfirm.length<6) setPinConfirm(pinConfirm+d); }
                }}>{d}</button>
              ))}
              <button className="btn pin-btn" style={{ background:"transparent", boxShadow:"none" }}/>
              <button className="btn pin-btn" onClick={()=>{
                if (pinStep===1) { if (pinInput.length<6) setPinInput(pinInput+"0"); }
                else { if (pinConfirm.length<6) setPinConfirm(pinConfirm+"0"); }
              }}>0</button>
              <button className="btn pin-btn" style={{ background:"transparent", boxShadow:"none" }} onClick={()=>{
                if (pinStep===1) setPinInput(pinInput.slice(0,-1));
                else setPinConfirm(pinConfirm.slice(0,-1));
              }}>⌫</button>
            </div>
            {pinError&&<p style={{ fontSize:12, color:T.red, marginBottom:12, textAlign:"center" }}>⚠️ {pinError}</p>}
            {pinModal==="setup"?(
              pinStep===1?(
                <button className="btn btn-primary" disabled={pinInput.length!==6}
                  onClick={()=>{ setPinStep(2); setPinConfirm(""); setPinError(""); }}
                  style={{ width:"100%", padding:"15px", fontSize:14 }}>Continue →</button>
              ):(
                <button className="btn btn-primary" disabled={pinConfirm.length!==6} onClick={handlePinSetup}
                  style={{ width:"100%", padding:"15px", fontSize:14 }}>
                  {loading&&<span className="spinner" style={{ borderTopColor:"#fff", borderColor:"rgba(255,255,255,.25)" }}/>}
                  {loading?"Saving...":"Set PIN"}
                </button>
              )
            ):(
              <button className="btn btn-primary" disabled={pinInput.length!==6} onClick={handlePinVerify}
                style={{ width:"100%", padding:"15px", fontSize:14 }}>
                {loading&&<span className="spinner" style={{ borderTopColor:"#fff", borderColor:"rgba(255,255,255,.25)" }}/>}
                {loading?"Verifying...":"Unlock"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Toast */}
      {toast&&(
        <div className="toast">
          <span>{toast.type==="error"?"⚠️":toast.type==="success"?"✅":"ℹ️"}</span>{toast.text}
        </div>
      )}

      {/* Bottom Nav */}
      <div className="bottomnav">
        {[
          {l:"Home",s:"home",ic:"🏠"},
          {l:"Send",s:"send",ic:"↗"},
          {l:"Add",s:"topup",ic:"+"},
          {l:"Activity",s:"activity",ic:"≣"},
          {l:"Alerts",s:"notifications",ic:"🔔"},
        ].map(i=>(
          <button key={i.l} className={`btn navb ${screen===i.s?"on":""}`}
            onClick={()=>{ setScreen(i.s); if(i.s==="notifications")doMarkRead(); }}>
            <span style={{ fontSize:19 }}>{i.ic}</span>{i.l}
            {i.s==="notifications"&&unread>0&&<span style={{ position:"absolute", top:8, right:"calc(50% - 18px)", background:T.red, color:"#fff", fontSize:10, fontWeight:800, borderRadius:10, padding:"1px 6px" }}>{unread}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
